import React, { useState, useEffect } from "react";
import styled from "styled-components";
import FeedbackRequestForm from "./components/FeedbackRequestForm.jsx";
import FeedbackRequestList from "./components/FeedbackRequestList.jsx";
import * as feedbackService from "./services/feedbackService.js";

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
`;

const Button = styled.button`
  background: #007bff;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #0056b3;
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
`;

const Alert = styled.div`
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  background: ${(props) => (props.type === "success" ? "#d4edda" : "#f8d7da")};
  color: ${(props) => (props.type === "success" ? "#155724" : "#721c24")};
  border: 1px solid
    ${(props) => (props.type === "success" ? "#c3e6cb" : "#f5c6cb")};
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 48px;
  color: #6c757d;
`;

const FeedbackPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  // Load feedback requests on component mount
  useEffect(() => {
    loadFeedbackRequests();
  }, []);

  const loadFeedbackRequests = async () => {
    setLoading(true);
    try {
      const result = await feedbackService.getFeedbackRequests();
      if (result.success) {
        setRequests(result.data);
      } else {
        setAlert({
          type: "error",
          message: result.error || "Failed to load feedback requests",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (formData) => {
    setLoading(true);
    try {
      const result = await feedbackService.createFeedbackRequest(formData);
      if (result.success) {
        setAlert({
          type: "success",
          message: "Feedback request created successfully!",
        });
        setShowForm(false);
        // Reload the list
        await loadFeedbackRequests();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (requestId) => {
    setLoading(true);
    try {
      const result = await feedbackService.sendFeedbackRequest(requestId);
      if (result.success) {
        setAlert({
          type: "success",
          message: "Feedback request sent successfully!",
        });
        // Reload the list
        await loadFeedbackRequests();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (
      !window.confirm("Are you sure you want to cancel this feedback request?")
    ) {
      return;
    }

    setLoading(true);
    try {
      const result = await feedbackService.cancelFeedbackRequest(requestId);
      if (result.success) {
        setAlert({
          type: "success",
          message: "Feedback request cancelled successfully!",
        });
        // Reload the list
        await loadFeedbackRequests();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setAlert(null);
  };

  // Clear alert after 5 seconds
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  return (
    <Container>
      <Header>
        <Title>General Experience Feedback</Title>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>
            Request General Feedback
          </Button>
        )}
      </Header>

      {alert && <Alert type={alert.type}>{alert.message}</Alert>}

      <Content>
        {showForm ? (
          <FeedbackRequestForm
            onSubmit={handleCreateRequest}
            onCancel={handleCancelForm}
            loading={loading}
          />
        ) : loading ? (
          <LoadingSpinner>
            <p>Loading feedback requests...</p>
          </LoadingSpinner>
        ) : (
          <FeedbackRequestList
            requests={requests}
            onSendRequest={handleSendRequest}
            onCancelRequest={handleCancelRequest}
            loading={loading}
          />
        )}
      </Content>
    </Container>
  );
};

export default FeedbackPage;
